import type { SyncStats } from "lib/api/rowIdentityWire";

enum TaskType {
  ExportTable = "ExportTable",
  ExportView = "ExportView",
  ExportClipboard = "ExportClipboard",
  DsvSniffAndPreview = "DsvSniffAndPreview",
  DsvReparse = "DsvReparse",
  DsvImport = "DsvImport",
  DsvFastImport = "DsvFastImport",
  ParquetImport = "ParquetImport",
  ClientDataRequest = "ClientDataRequest",
  ClientDataRequestComplete = "ClientDataRequestComplete",
  ClientDataRequestFailed = "ClientDataRequestFailed",
  SessionExpired = "SessionExpired",
  // Local SQLite connector (T7 of the plan). Each TaskType maps to one
  // method on the worker's SqliteConnection registry. The registry is
  // keyed by `saved_connection_id`, so list/describe/import/close all
  // require an `Open` to have run first.
  SqliteAttachToOpfs = "SqliteAttachToOpfs",
  SqliteOpenConnection = "SqliteOpenConnection",
  SqliteListObjects = "SqliteListObjects",
  SqliteDescribeObject = "SqliteDescribeObject",
  SqliteImportTable = "SqliteImportTable",
  SqliteCloseConnection = "SqliteCloseConnection",
  SqliteClearOpfsCache = "SqliteClearOpfsCache",
  SqliteCheckSourceFreshness = "SqliteCheckSourceFreshness",
  // v1.1 Sync action — re-attach + re-read + POST to
  // /tables/local_sqlite/sync in one worker round trip. Replies carry
  // the freshly-computed SHA-256 and the server's snapshot/source so
  // the main thread can stamp the model state and mark freshness.
  SqliteSyncTable = "SqliteSyncTable",
}

export enum MessageType {
  Progress = "progress",
  Status = "status",
  Error = "error",
}

export enum OperationType {
  Upload = "upload",
  Download = "download",
}

export enum ErrorType {
  Quota = "quota",
  Upload = "upload",
  Download = "download",
}

/**
 * Which transport carried a successful upload.
 *  - "streaming"          — duplex:"half" ReadableStream POST (modern Chromium/Firefox over HTTP/2)
 *  - "buffered"           — Safari direct buffered POST (DUPLEX_SUPPORTED=false)
 *  - "buffered_fallback"  — streaming attempted then failed; recovered via buffered POST
 *                            (signal that a corporate firewall / TLS-MITM proxy is in play)
 */
export type TransportKind = "streaming" | "buffered" | "buffered_fallback";

interface BaseOperationMessage {
  filename: string;
  operation: OperationType;
}

export interface WorkerProgressMessage extends BaseOperationMessage {
  type: MessageType.Progress;
  progress: number;
}

export interface WorkerStatusMessage extends BaseOperationMessage {
  type: MessageType.Status;
  success: boolean;
  message: string;
  modelID?: string;
  tableID?: string;
  transport?: TransportKind;
}

export interface WorkerErrorMessage extends BaseOperationMessage {
  type: MessageType.Error;
  errorType: ErrorType;
  error: string;
  message: string;
  /** Whether trying again is safe. Absent means it is not: an upload mints a
   *  table per call with no idempotency key, so a retry after the write began
   *  makes a second copy. Only the worker can tell a refusal issued before any
   *  write from a failure that arrived after one. */
  retryable?: boolean;
}

export type WorkerOperationMessage =
  WorkerProgressMessage | WorkerStatusMessage | WorkerErrorMessage;

interface ClientDataRequestMessage {
  request_id: string;
  request: string;
  table_id?: string;
}
/** Output format for a table download. Defaults to `csv` when omitted. */
export type ExportFileFormat = "csv" | "parquet";

interface ExportTableMessage {
  endpoint?: string;
  tableID: string;
  tableName: string;
  version: number | null;
  colSettings: Record<string, { timezone?: string; decimal_places?: number }>;
  columnNames: Record<string, string>;
  categoryLabels?: Record<string, Record<number, string>>;
  attachmentColumns?: string[];
  totalRows?: number;
  format?: ExportFileFormat;
}

interface ExportViewMessage {
  endpoint?: string;
  viewName: string;
  version: number | null;
  tableView: Record<string, unknown>;
  colSettings: Record<string, { timezone?: string; decimal_places?: number }>;
  columnNames: Record<string, string>;
  categoryLabels?: Record<string, Record<number, string>>;
  attachmentColumns?: string[];
  totalRows?: number;
  format?: ExportFileFormat;
}

/** Column ids of the Attachment columns, so the export writes file names (A8). */
export function attachmentColumnIds(
  columns: Array<{ id: string; data_type: unknown }>,
  attachmentType: unknown,
): string[] {
  return columns.filter((c) => c.data_type === attachmentType).map((c) => c.id);
}

/** Build column name and display settings maps for CSV/TSV export.
 *  Used by both TableDetails and TableViewDetails download handlers. */
export function buildExportColumnMaps(
  columns: {
    id: string;
    name?: string | null;
    display_timezone?: string | null;
    decimal_places?: number | null;
  }[],
): {
  columnNames: Record<string, string>;
  colSettings: Record<string, { timezone?: string; decimal_places?: number }>;
} {
  const columnNames: Record<string, string> = {};
  const colSettings: Record<
    string,
    { timezone?: string; decimal_places?: number }
  > = {};
  for (const col of columns) {
    columnNames[col.id] = col.name ?? "";
    if (col.display_timezone || col.decimal_places != null) {
      colSettings[col.id] = {
        timezone: col.display_timezone ?? undefined,
        decimal_places: col.decimal_places ?? undefined,
      };
    }
  }
  return { columnNames, colSettings };
}

interface ExportClipboardMessage {
  requestBody: string;
  colSettingsJson: string;
  columnNamesJson: string;
  categoryLabelsJson?: string;
  attachmentColumnsJson?: string;
}

export interface ImportConfig {
  delimiter: number;
  quote: number | null;
  escape?: number;
  has_header: boolean;
  skip_rows?: number;
  /** A label from `EncodingOption`; omitted = let the worker detect. */
  encoding?: string;
  /** Settles columns whose numbers read as `1,5` or `1 500` either way. */
  decimal_separator?: DecimalSeparator;
}

export type DecimalSeparator = "comma" | "dot";

export interface SniffResult {
  delimiter: number;
  quote: number;
  has_header: boolean;
  encoding: string;
  /** False = the worker's best guess; the person should check the accents. */
  encoding_confident: boolean;
}

export interface EncodingOption {
  label: string;
  name: string;
}

export interface PreviewColumn {
  name: string;
  inferred_type: string;
}

export interface PreviewResult {
  columns: PreviewColumn[];
  total_rows_in_sample: number;
  rows_consistent: boolean;
  non_ascii_samples: string[];
  /** Columns left as text because only a `decimal_separator` pick can settle them. */
  ambiguous_number_columns: string[];
  /** Columns a spreadsheet rounded before export; kept as text, the digits are gone. */
  rounded_identifier_columns: string[];
}

export interface DsvSniffAndPreviewResult {
  sniff: SniffResult;
  preview: PreviewResult;
  encodings: EncodingOption[];
}

/** Why the drop fast route handed the file to the Upload panel instead. */
export type FastImportFallbackReason =
  | "inconsistent_rows"
  | "encoding_uncertain"
  | "legacy_encoding"
  | "number_format_uncertain"
  | "rounded_identifiers";

interface DsvSniffAndPreviewMessage {
  file: File;
}

interface DsvReparseMessage {
  config: ImportConfig;
}

interface DsvImportMessage {
  config: ImportConfig;
  modelID: string;
  filename: string;
}

interface DsvFastImportMessage {
  sampleBytes: ArrayBuffer;
  fileBytes: ArrayBuffer;
  modelID: string;
  filename: string;
  /** "batch" marks a file the import queue owns: the dropzone must not steal
   *  its fallback into the single-file panel. */
  source?: "drop" | "paste" | "batch";
}

interface ParquetImportMessage {
  fileBytes: ArrayBuffer;
  modelID: string;
  filename: string;
}

// ---------------------------------------------------------------------------
// Local SQLite (T7-T11). The five TaskTypes map to lifecycle and import
// methods on a `SqliteConnection` held by the worker's registry. All
// require a prior `SqliteOpenConnection` for the same
// `saved_connection_id`; the worker rejects with `error: "not open"`
// otherwise.
// ---------------------------------------------------------------------------

/** Object listed in a SQLite database's `sqlite_schema`. `kind: "view"`
 *  also imports as a snapshot — we run `SELECT * FROM <view>`. */
export interface SqliteObject {
  name: string;
  kind: "table" | "view";
  column_count: number;
}

/** A single column from `PRAGMA table_info`. */
export interface SqliteColumn {
  cid: number;
  name: string;
  declared_type: string;
  notnull: boolean;
  pk: number;
}

/** A foreign-key edge from `PRAGMA foreign_key_list`. v1 silently skips
 *  composite FKs at the worker level — the auto-link orchestrator
 *  filters them out before calling create_link. */
export interface SqliteForeignKey {
  id: number;
  seq: number;
  referenced_table: string;
  from_column: string;
  to_column: string;
}

export interface SqliteObjectDescription {
  name: string;
  kind: "table" | "view";
  columns: SqliteColumn[];
  foreign_keys: SqliteForeignKey[];
  /** Columns carrying a SINGLE-column, non-PK, non-partial UNIQUE index
   *  (`PRAGMA index_list`/`index_info`). Composite uniques are omitted — no
   *  single-column distinct intent represents them. */
  unique_columns: string[];
  /** Every total unique column set (composites included). Optional so
   *  fixtures and older producers stay valid; absent ⇒ none known. */
  unique_constraints?: string[][];
  /** Exact row count from `SELECT COUNT(*)` at describe time. Views
   *  resolve the underlying query, so this can be slow for expensive
   *  views; in practice OPFS reads stay well under the 300ms hover
   *  delay for v1-scale files. */
  row_count: number;
}

/** Normalized source-database constraints — mirror of the Rust
 *  `common_core::connection::source_constraints::SourceConstraints` (serde
 *  snake_case wire shape). The SQLite worker builds this from PRAGMA reads and
 *  ships it in the import/sync request; the server folds the resolved intents
 *  onto the snapshot. Column references are SOURCE field names. */
export interface SourceConstraints {
  non_null_columns: string[];
  primary_key_columns: string[];
  /** Single-column, non-PK uniques only (composite uniques are unrepresentable). */
  unique_columns: string[];
  /** Every total unique column set (composites included). Optional so
   *  fixtures and older producers stay valid; absent ⇒ none known. */
  unique_constraints?: string[][];
  foreign_keys: SourceForeignKey[];
}

/** One foreign key, single- or multi-column. `from_columns[i]` references
 *  `to_columns[i]`. Mirror of Rust `SourceForeignKey`. */
export interface SourceForeignKey {
  from_columns: string[];
  /** PG schema of the referenced table; null for SQLite (no schema namespace). */
  referenced_schema: string | null;
  referenced_table: string;
  to_columns: string[];
}

/** First-time attach — copy the source file into OPFS and hash it
 *  in one pass. No saved-connection ID exists yet; the worker returns
 *  the fresh `opfsName` for the main thread to record once the server
 *  issues a `saved_connection_id`. */
interface SqliteAttachToOpfsMessage {
  handle: FileSystemFileHandle;
  /** OPFS filenames eligible for LRU eviction on QuotaExceededError,
   *  oldest first (lowest `lastAttachedAt`). Empty when the device has
   *  no other cached copies. */
  evictionCandidates: string[];
}

interface SqliteOpenConnectionMessage {
  savedConnectionId: string;
  handle: FileSystemFileHandle;
  /** OPFS filename from the IDB record, or `null` if no cached copy
   *  exists yet (first open on this device, or the cache was
   *  evicted). When `null`, the worker re-copies from `handle`. */
  knownOpfsName: string | null;
  /** Same shape as the first-attach message: opfs names eligible
   *  for LRU eviction, oldest first. */
  evictionCandidates: string[];
}

interface SqliteListObjectsMessage {
  savedConnectionId: string;
}

interface SqliteDescribeObjectMessage {
  savedConnectionId: string;
  objectName: string;
}

interface SqliteImportTableMessage {
  savedConnectionId: string;
  objectName: string;
  modelID: string;
  /** v1.2.2 — 64-char lowercase hex SHA-256 of the source file. The
   *  main thread reads it from the IDB handle record and passes it
   *  through; the worker forwards it to the server's
   *  `/tables/ipc/create` so `StoredLocalSqliteMetadata.source_sha256`
   *  and `LocalSqliteDatabase.source_sha256` get stamped. The worker
   *  echoes it on the reply so the main thread's `LocalSqliteDatabase`
   *  stamp carries the same hash. Empty string for the pre-v1.2.2
   *  fallback (no hash known). */
  sourceSha256Hex: string;
  /** Canvas-space drop point when the import came from a drag onto the
   *  canvas; echoed on the reply so the main-thread listener can pass it
   *  to `handle_local_sqlite_load`. Absent for button imports. */
  at?: { x: number; y: number };
}

/** v1.1 Sync action message. `requestId` is echoed back so the
 *  caller (a one-shot listener on the main thread) can resolve the
 *  matching promise. `handle` is taken straight from the IDB record
 *  for `savedConnectionId`. */
interface SqliteSyncTableMessage {
  requestId: string;
  savedConnectionId: string;
  sqliteTableName: string;
  handle: FileSystemFileHandle;
  modelID: string;
  tableID: string;
  connectionID: string;
  tableVersion: number;
  /** JSON `IdentityDecision`, answer to a previous 409 `identity_conflict`. */
  identityDecisionJson?: string | null;
  /** OPFS cache hint — opfs filename last used for this connection.
   *  `null` if the device has no cached copy yet (cache miss → fresh
   *  copy). The worker forces a re-copy regardless on Sync, so this
   *  is informational only (still passed for the LRU eviction logic
   *  on `QuotaExceededError`). */
  knownOpfsName: string | null;
  evictionCandidates: string[];
}

interface SqliteCloseConnectionMessage {
  savedConnectionId: string;
}

interface SqliteClearOpfsCacheMessage {
  /** OPFS filename to unlink from the SAH pool. The connection (if
   *  any) for `savedConnectionId` should already be closed — calling
   *  this with a live connection is undefined behavior per upstream. */
  savedConnectionId: string;
  opfsName: string;
}

/** Source-file freshness check (v1.2.1). Re-hashes the user's source
 *  `.db` and compares to the stored SHA so the freshness store can
 *  light up the "Source has changed · Sync" indicator on imported
 *  tables. */
interface SqliteCheckSourceFreshnessMessage {
  savedConnectionId: string;
  handle: FileSystemFileHandle;
  /** SHA-256 hex stored in IDB at last attach/sync. The worker
   *  compares the freshly-computed hash against this. */
  expectedSha256: string;
}

export interface SqliteAttachToOpfsResult {
  opfsName: string;
  sha256: string;
  fileSize: number;
  /** OPFS files unlinked to make room. Main thread should null out
   *  the `opfsName` field on each corresponding IDB record. */
  evicted: string[];
}

export interface SqliteOpenConnectionResult {
  savedConnectionId: string;
  ok: true;
  /** OPFS filename now active for this connection (matches
   *  `knownOpfsName` on cache-hit; freshly generated on cache-miss). */
  opfsName: string;
  /** `true` iff the worker re-copied the source on this open. When
   *  `true`, `sha256` and `fileSize` are also present; main thread
   *  should patch the IDB record. */
  copied: boolean;
  sha256?: string;
  fileSize?: number;
  /** OPFS files unlinked to make room (LRU eviction). Main thread
   *  should null out the `opfsName` field on each corresponding IDB
   *  record. */
  evicted: string[];
}

export interface SqliteClearOpfsCacheResult {
  savedConnectionId: string;
  opfsName: string;
  /** `true` iff the file was actually present in the pool when the
   *  unlink ran. Idempotent: a `false` result is a no-op success. */
  unlinked: boolean;
}

export type SqliteCheckSourceFreshnessError =
  "permission-denied" | "file-gone" | "io";

export interface SqliteCheckSourceFreshnessResult {
  savedConnectionId: string;
  /** `true` when the freshly-computed SHA-256 matches `expectedSha256`
   *  AND the file is currently readable. `false` when the source has
   *  changed OR when an error occurred (see `error`). */
  fresh: boolean;
  /** Freshly-computed SHA-256, or `null` if the worker couldn't read
   *  the file (e.g., permission denied, file removed). */
  currentSha256: string | null;
  fileSize: number;
  lastModified: number;
  /** Set when the read failed. Callers treat the connector as
   *  `unavailable` in the freshness store and direct the user to the
   *  Connections page re-attach prompt. */
  error?: SqliteCheckSourceFreshnessError;
}

export interface SqliteListObjectsResult {
  savedConnectionId: string;
  objects: SqliteObject[];
}

export interface SqliteDescribeObjectResult extends SqliteObjectDescription {
  savedConnectionId: string;
}

export interface ProposedLinkResult {
  fromTableId: string;
  /** Child-side column(s), key order. Length > 1 for composite FKs. */
  fromColumnIds: string[];
  toTableId: string;
  /** Parent-side column(s), positionally paired with `fromColumnIds`. */
  toColumnIds: string[];
}

/** A `(MyCorr ColumnID, source SQLite column name)` pair returned by
 *  the server in `column_mapping` after `/tables/ipc/create`. The main
 *  thread needs this verbatim to construct the WASM `LocalSqliteDatabase`
 *  source. Shape matches the server's `common_core::ColumnMapping`. */
export interface SqliteColumnMappingEntry {
  id: string;
  name: string | null;
}

/** Server response for a successful sync. Mirrors the Rust
 *  `SyncLocalSqliteResponse`. `updated_source` carries the new
 *  `LocalSqliteDatabase` payload (post-sync `column_mapping` and the
 *  echoed `sqlite_table_name`). */
export interface SqliteSyncResponseBody {
  updated_snapshot: unknown;
  updated_source: {
    connection_id_str: string;
    column_mapping_str: Array<{ id: string; name: string | null }>;
    saved_connection_id?: string | null;
    sqlite_table_name?: string;
    /** v1.2.2 — 64-char lowercase hex SHA-256 of the source file
     *  (serialized via the `sha256_hex_opt` custom serde module on
     *  `LocalSqliteDatabase.source_sha256`). Omitted by pre-v1.2.2
     *  servers; the LocalSqliteDatabase constructor then receives ""
     *  and stamps `None`. */
    source_sha256?: string;
  };
  schema_update: boolean;
  meta_update: boolean;
  data_version: number;
  no_change?: boolean;
  row_identity?: unknown;
  sync_stats?: SyncStats | null;
}

export interface SqliteSyncTableResult {
  requestId: string;
  savedConnectionId: string;
  /** Freshly-computed SHA-256 of the source file. Drives the
   *  freshness store's `markFresh` so the "Source has changed" badge
   *  clears after sync. */
  sha256: string;
  /** OPFS filename now active for the connection. Main thread should
   *  patch the IDB record if it differs from the prior `opfsName`. */
  opfsName: string;
  fileSize: number;
  /** OPFS files unlinked to make room during the re-copy (LRU
   *  eviction). Main thread should null `opfsName` on each
   *  corresponding IDB record. */
  evicted: string[];
  syncResponse: SqliteSyncResponseBody;
  /** Grouped source FKs (PRAGMA-derived) for the synced table. FK auto-link
   *  resolves on the main thread via `resolveSourceFkLinks` (one resolver for
   *  every SQL connector). */
  foreign_keys: SourceForeignKey[];
}

export interface SqliteImportTableResult {
  savedConnectionId: string;
  objectName: string;
  tableID: string;
  /** Grouped source FKs (PRAGMA-derived) for the imported table. FK auto-link
   *  resolves on the main thread via `resolveSourceFkLinks` (forward + backward
   *  bidirectional scan), shared with Postgres/Supabase. */
  foreign_keys: SourceForeignKey[];
  /** Per-table `ConnectionID` the server minted when persisting
   *  `StoredLocalSqliteMetadata`. `null` if the metadata persist
   *  failed — main thread falls back to leaving the source as Owned. */
  connectionId: string | null;
  /** Column mapping straight from the server response. Used by the
   *  main thread (with `connectionId`) to build the `LocalSqliteDatabase`
   *  source for `handle_local_sqlite_load`. */
  columnMapping: SqliteColumnMappingEntry[];
  /** Source SQLite table/view name echoed by the server. Used by the
   *  main thread to stamp `LocalSqliteDatabase.sqlite_table_name`, so
   *  the v1.1 Sync action can SELECT rows from this object without an
   *  extra server round-trip. `null` when the upload was not a Local
   *  SQLite import (CSV / Parquet / paste). */
  sqliteTableName: string | null;
  /** v1.2.2 — echoed back to the main thread so the
   *  `LocalSqliteDatabase` constructor receives the same hash the
   *  server stamped on `LocalSqliteDatabase.source_sha256`. Empty
   *  string when the import was not a Local SQLite upload or the
   *  client pre-dates v1.2.2 (no hash known). */
  sourceSha256Hex: string;
  /** Echoed from the request — canvas-space drop point for a canvas drag,
   *  passed to `handle_local_sqlite_load` so the table lands under the
   *  cursor. */
  at?: { x: number; y: number };
}

export interface SqliteCloseConnectionResult {
  savedConnectionId: string;
  ok: true;
}

export interface SqliteErrorResult {
  error: string;
}

type ToWorkerMessage =
  | { task: TaskType.ExportTable; data: ExportTableMessage }
  | { task: TaskType.ExportView; data: ExportViewMessage }
  | { task: TaskType.ExportClipboard; data: ExportClipboardMessage }
  | { task: TaskType.ClientDataRequest; data: ClientDataRequestMessage }
  | { task: TaskType.DsvSniffAndPreview; data: DsvSniffAndPreviewMessage }
  | { task: TaskType.DsvReparse; data: DsvReparseMessage }
  | { task: TaskType.DsvImport; data: DsvImportMessage }
  | { task: TaskType.DsvFastImport; data: DsvFastImportMessage }
  | { task: TaskType.ParquetImport; data: ParquetImportMessage }
  | { task: TaskType.SqliteAttachToOpfs; data: SqliteAttachToOpfsMessage }
  | { task: TaskType.SqliteOpenConnection; data: SqliteOpenConnectionMessage }
  | { task: TaskType.SqliteListObjects; data: SqliteListObjectsMessage }
  | { task: TaskType.SqliteDescribeObject; data: SqliteDescribeObjectMessage }
  | { task: TaskType.SqliteImportTable; data: SqliteImportTableMessage }
  | { task: TaskType.SqliteSyncTable; data: SqliteSyncTableMessage }
  | {
      task: TaskType.SqliteCloseConnection;
      data: SqliteCloseConnectionMessage;
    }
  | {
      task: TaskType.SqliteClearOpfsCache;
      data: SqliteClearOpfsCacheMessage;
    }
  | {
      task: TaskType.SqliteCheckSourceFreshness;
      data: SqliteCheckSourceFreshnessMessage;
    };

interface DecompressMessage {
  action: string;
  compressed: Blob;
  tableName: string;
  /** Download extension without the dot ("csv" or "parquet"). */
  fileExtension: ExportFileFormat;
}

interface ClientDataResponseMessage {
  request_id: string;
  header: Record<string, unknown>;
  data: number[];
}

export interface ExportClipboardResult {
  tsvString: string;
}

export type FromWorkerMessage =
  | WorkerProgressMessage
  | WorkerStatusMessage
  | WorkerErrorMessage
  | { task: TaskType.ClientDataRequest; data: ClientDataResponseMessage }
  | { task: TaskType.ClientDataRequestComplete; data: { request_id: string } }
  | {
      task: TaskType.ClientDataRequestFailed;
      data: {
        request_id: string;
        /** Set when the door answered `values_not_readable` (D37): the table
         *  is blanked for this viewer and must stop re-requesting. */
        values_blanked_table_id?: string | null;
      };
    }
  | { task: TaskType.ExportTable; data: DecompressMessage }
  | { task: TaskType.ExportView; data: DecompressMessage }
  | { task: TaskType.ExportClipboard; data: ExportClipboardResult }
  | { task: TaskType.DsvSniffAndPreview; data: DsvSniffAndPreviewResult }
  | { task: TaskType.DsvReparse; data: PreviewResult }
  | {
      task: TaskType.DsvFastImport;
      data: {
        reason: FastImportFallbackReason;
        filename: string;
        source?: string;
      };
    }
  | { task: TaskType.SessionExpired }
  | {
      task: TaskType.SqliteAttachToOpfs;
      data: SqliteAttachToOpfsResult | SqliteErrorResult;
    }
  | {
      task: TaskType.SqliteOpenConnection;
      data: SqliteOpenConnectionResult | SqliteErrorResult;
    }
  | {
      task: TaskType.SqliteListObjects;
      data: SqliteListObjectsResult | SqliteErrorResult;
    }
  | {
      task: TaskType.SqliteDescribeObject;
      data: SqliteDescribeObjectResult | SqliteErrorResult;
    }
  | {
      task: TaskType.SqliteImportTable;
      data: SqliteImportTableResult | SqliteErrorResult;
    }
  | {
      task: TaskType.SqliteSyncTable;
      data: (SqliteSyncTableResult | SqliteErrorResult) & {
        requestId?: string;
      };
    }
  | {
      task: TaskType.SqliteCloseConnection;
      data: SqliteCloseConnectionResult | SqliteErrorResult;
    }
  | {
      task: TaskType.SqliteClearOpfsCache;
      data: SqliteClearOpfsCacheResult | SqliteErrorResult;
    }
  | {
      task: TaskType.SqliteCheckSourceFreshness;
      data: SqliteCheckSourceFreshnessResult | SqliteErrorResult;
    };

export type {
  ToWorkerMessage,
  DecompressMessage,
  ClientDataRequestMessage,
  ClientDataResponseMessage,
  ExportTableMessage,
  ExportViewMessage,
  ExportClipboardMessage,
  DsvSniffAndPreviewMessage,
  DsvReparseMessage,
  DsvImportMessage,
  DsvFastImportMessage,
  ParquetImportMessage,
};

export { TaskType };
